import { call, put, select } from 'redux-saga/effects'
import { API_RESPONSE_TYPE, IApiResponse } from 'shared/constants'
import {
  fetchOverallBusinessSettingsAction,
  fetchOverallBusinessSettingsSuccessAction,
  resetSettingsLoaders,
  setGoogleDriveLoadingAction,
  setGoogleDriveStatusAction,
  setStaffDistanceTravelled
} from 'state/settings/settings.action'
import {
  fetchGoogleDriveAbout,
  getGoogleDriveAccessToken,
  IGoogleDriveAbout,
  IGoogleDriveToken
} from 'services/Api/GoogleDriveApi'
import { GoogleDriveStorageInstance } from 'services/Storage/GoogleDriveStorage'
import { updateIndividualUser } from 'services/Api/IdentityApi'
import {
  TImageBackupDestination,
  setImageBackupDestination
} from 'services/ImageBackup/ImageBackupPreference'
import { Action } from 'infra/types'
import { IAppState } from 'infra/AppState'
import { IDashboardState } from 'state/dashboard/dashboard.types'
import {
  IBusinessSettingPayload,
  IBusinessSettingsResponse
} from 'services/Api/BillingApiFolder.ts/type'
import {
  fetchOverallBusinessSettingsApi,
  updateBusinessSettingsApi
} from 'services/Api/BillingApiFolder.ts/api'
import { addAutoFadeNotification } from 'pages/Notification/Notification.actions'
import { NotificationType } from 'pages/Notification/Notification.types'
import { getStaffLocationLogs } from 'services/Api/StaffLinkApiFolder/api'
import { haversineFormula } from 'state/settings/settings.helpers'

export function* fetchOverallBusinessSettingsEffect() {
  const response: IApiResponse<{ settings: IBusinessSettingsResponse }> = yield call(
    fetchOverallBusinessSettingsApi
  )

  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Could not fetch settings. Please try again later.', {
      cause: 'customError'
    })
  }
  yield put(fetchOverallBusinessSettingsSuccessAction(response.data.settings))
}

export function* updateBusinessSettingsEffect(action: Action<{ id: string; value: boolean }>) {
  const { merchantProfile }: IDashboardState = yield select((app: IAppState) => app.Dashboard)
  const merchantId = merchantProfile?.data?.id || ''
  const payload: IBusinessSettingPayload = {
    business_id: merchantId,
    settings: {
      [action.payload.id]: action.payload.value
    }
  }
  const response: IApiResponse = yield call(updateBusinessSettingsApi, payload)

  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    yield put(resetSettingsLoaders())
    throw new Error('Could not update settings. Please try again later.', {
      cause: 'customError'
    })
  }
  yield put(fetchOverallBusinessSettingsAction())
  yield put(resetSettingsLoaders())
}

export function* trackStaffLocationEffect(action: Action<string>) {
  const startTime = new Date()
  startTime.setHours(0, 0, 0, 0)
  startTime.setDate(startTime.getDate())

  const endTime = new Date()
  endTime.setHours(23, 59, 59, 999)

  const response: IApiResponse = yield call(
    getStaffLocationLogs,
    action.payload,
    Math.floor(startTime.getTime() / 1000),
    Math.floor(endTime.getTime() / 1000)
  )

  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Could not fetch staff location logs. Please try again later.', {
      cause: 'customError'
    })
  }

  const logs = response?.data?.staff_location_logs || []
  if (logs.length > 0) {
    logs.sort((a: any, b: any) => a.recorded_at - b.recorded_at)
    const baseUrl = 'https://www.google.com/maps/dir/'
    const coordinates = logs
      .map((log: any) => `${log.latitude},${log.longitude}`)
      .reverse()
      .join('/')
    const mapsUrl = `${baseUrl}${coordinates}/@${logs[logs.length - 1].latitude},${
      logs[logs.length - 1].longitude
    },13.0z`

    window.open(mapsUrl, '_blank')

    yield put(
      addAutoFadeNotification({
        type: NotificationType.SUCCESS,
        bodyText: 'Staff location logs fetched successfully'
      })
    )
  } else {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.INFO,
        bodyText: 'No staff location logs found'
      })
    )
  }
}

export function* fetchGoogleDriveStatusEffect() {
  yield put(setGoogleDriveLoadingAction(true))
  const lastSyncTime = GoogleDriveStorageInstance.getLastSyncTime()
  try {
    const tokenResponse: IApiResponse<IGoogleDriveToken> = yield call(getGoogleDriveAccessToken)
    if (tokenResponse.type !== API_RESPONSE_TYPE.SUCCESS || !tokenResponse.data?.access_token) {
      yield put(
        setGoogleDriveStatusAction({
          connected: false,
          email: null,
          displayName: null,
          photoUrl: null,
          usageBytes: null,
          limitBytes: null,
          lastCheckedAt: Date.now(),
          lastSyncTime
        })
      )
      return
    }
    const aboutResponse: IApiResponse<IGoogleDriveAbout> = yield call(
      fetchGoogleDriveAbout,
      tokenResponse.data.access_token
    )
    if (aboutResponse.type !== API_RESPONSE_TYPE.SUCCESS) {
      yield put(
        setGoogleDriveStatusAction({
          connected: true,
          email: null,
          displayName: null,
          photoUrl: null,
          usageBytes: null,
          limitBytes: null,
          lastCheckedAt: Date.now(),
          lastSyncTime
        })
      )
      return
    }
    const { user, storageQuota } = aboutResponse.data
    yield put(
      setGoogleDriveStatusAction({
        connected: true,
        email: user?.emailAddress || null,
        displayName: user?.displayName || null,
        photoUrl: user?.photoLink || null,
        usageBytes: storageQuota?.usage ? Number(storageQuota.usage) : null,
        limitBytes: storageQuota?.limit ? Number(storageQuota.limit) : null,
        lastCheckedAt: Date.now(),
        lastSyncTime
      })
    )
  } finally {
    yield put(setGoogleDriveLoadingAction(false))
  }
}

export function* updateImageBackupDestinationEffect(action: Action<TImageBackupDestination>) {
  const destination = action.payload
  const { Dashboard, Settings }: IAppState = yield select((app: IAppState) => app)
  const individualUser = Dashboard.businessAccounts?.individualUser
  const previous = Settings.gdrive.imageBackupDestination

  if (destination === previous) {
    return
  }
  if (!individualUser?.id) {
    throw new Error('Could not update backup setting. Please try again later.', {
      cause: 'customError'
    })
  }

  yield put(setGoogleDriveStatusAction({ imageBackupDestination: destination }))
  setImageBackupDestination(destination)

  const response: IApiResponse = yield call(updateIndividualUser, {
    individual_user_id: individualUser.id,
    individual_user: {
      user: { id: individualUser.id, mobile: individualUser.mobile },
      image_backup_destination: destination
    },
    update_image_backup_destination: true
  })

  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    yield put(setGoogleDriveStatusAction({ imageBackupDestination: previous }))
    setImageBackupDestination(previous)
    throw new Error('Could not update backup setting. Please try again later.', {
      cause: 'customError'
    })
  }
}

export function* backupGoogleDriveNowEffect() {
  const now = Date.now()
  GoogleDriveStorageInstance.setLastSyncTime(now)
  yield put(setGoogleDriveStatusAction({ lastSyncTime: now }))
  yield call(fetchGoogleDriveStatusEffect)
}

export function* calculateStaffsDistanceTravelledEffect(action: Action<string>) {
  const startTime = new Date()
  startTime.setHours(0, 0, 0, 0)
  startTime.setDate(startTime.getDate())

  const endTime = new Date()
  endTime.setHours(23, 59, 59, 999)

  const response: IApiResponse = yield call(
    getStaffLocationLogs,
    action.payload,
    Math.floor(startTime.getTime() / 1000),
    Math.floor(endTime.getTime() / 1000)
  )

  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Could not fetch staff location logs. Please try again later.', {
      cause: 'customError'
    })
  }

  const logs = response?.data?.staff_location_logs || []
  if (logs.length > 0) {
    const distance = haversineFormula(logs)
    if (distance > 0) {
      yield put(setStaffDistanceTravelled({ staffId: action.payload, distance }))
      yield put(
        addAutoFadeNotification({
          type: NotificationType.SUCCESS,
          bodyText: 'Staff location logs fetched successfully'
        })
      )
    } else if (distance === -1) {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.INFO,
          bodyText: 'Something went wrong!!!'
        })
      )
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.INFO,
          bodyText: 'Distance Travelled is less than 0 KM for today!!!'
        })
      )
    }
  } else {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.INFO,
        bodyText: 'No staff location logs found'
      })
    )
  }
}
